// The one-window site form: the wizard's basics plus "activate now". What the
// customer wizard spreads over five steps, an admin does in one — activation
// only ever required a name and a country, and everything else stays editable
// on the live project (the activate gate's reasoning is in ProjectsService).
import { useState } from "react";
import { countryCodes, countryLabel, type CountryCode } from "@gb/contracts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/gb/field";
import { useCreateProject } from "./projects-api";

export function SiteCreateForm({
  tenantId,
  onCreated,
}: {
  tenantId: string;
  onCreated?: (projectId: string) => void;
}) {
  const [name, setName] = useState("");
  const [country, setCountry] = useState<CountryCode | "">("");
  const [region, setRegion] = useState("");
  const [spec, setSpec] = useState("");
  const [startDate, setStartDate] = useState("");
  const [targetHandoverDate, setTargetHandoverDate] = useState("");
  const [activate, setActivate] = useState(true);
  const create = useCreateProject(tenantId);

  const canSubmit = Boolean(name.trim()) && (!activate || Boolean(country)) && !create.isPending;

  const submit = (): void => {
    if (!canSubmit) return;
    create.mutate(
      {
        basics: {
          name: name.trim(),
          country: country || undefined,
          region: region.trim() || undefined,
          spec: spec.trim() || undefined,
          startDate: startDate || undefined,
          targetHandoverDate: targetHandoverDate || undefined,
        },
        activate,
      },
      { onSuccess: (project) => onCreated?.(project.id) },
    );
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Field label="Site name" error={create.isError ? String(create.error.message) : undefined}>
        {(props) => (
          <Input
            {...props}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Sunrise Park A"
          />
        )}
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field
          label="Country"
          optional={!activate}
          hint={activate ? "Required to activate — it selects the compliance regime." : undefined}
        >
          {(props) => (
            <select
              {...props}
              value={country}
              onChange={(e) => setCountry(e.target.value as CountryCode | "")}
              className="min-h-[46px] rounded-input border-[1.5px] border-border bg-white px-3 text-[0.9rem] text-ink"
            >
              <option value="">—</option>
              {countryCodes().map((code) => (
                <option key={code} value={code}>
                  {countryLabel(code)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Region" optional>
          {(props) => (
            <Input
              {...props}
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder="Vysočina"
            />
          )}
        </Field>
      </div>

      <Field label="Spec" optional hint="Capacity or a short description, e.g. 12.4 MWp.">
        {(props) => (
          <Input {...props} value={spec} onChange={(e) => setSpec(e.target.value)} />
        )}
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Start date" optional>
          {(props) => (
            <Input
              {...props}
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          )}
        </Field>
        <Field label="Target handover" optional>
          {(props) => (
            <Input
              {...props}
              type="date"
              value={targetHandoverDate}
              onChange={(e) => setTargetHandoverDate(e.target.value)}
            />
          )}
        </Field>
      </div>

      <label className="flex items-center gap-2 text-[0.85rem] text-ink">
        <input
          type="checkbox"
          checked={activate}
          onChange={(e) => setActivate(e.target.checked)}
          className="size-4 accent-brand"
        />
        Activate now (skip the tenant-side setup wizard)
      </label>

      <Button type="submit" size="sm" disabled={!canSubmit}>
        {create.isPending ? "Creating…" : activate ? "Create & activate site" : "Create site"}
      </Button>
    </form>
  );
}
